/**
 * Reading marks — the only honest source of "unread" this product has.
 *
 * Nothing is delivered to anybody here. A knock is a record the door holds; the
 * server never learns that a browser rendered it, and there is no `read_at` to
 * ask for. So unread is not a server fact, and asking the api to grow one would
 * be inventing state to justify a badge.
 *
 * It is instead exactly what it looks like: this browser's memory of when it
 * last opened each conversation. A row is unread when the conversation has
 * moved since. That is local, per-device and slightly wrong on a second
 * machine — which is the truth about a mark this product never synchronised,
 * and a badge that overstates by one on a new laptop is a smaller lie than a
 * server field that claims to know what a person has read.
 *
 * Deliberately NOT part of `lib/draft.ts`. That module is the round trip across
 * Stripe and the identity provider and is load-bearing; this is a convenience
 * that may fail silently, and the two must not share a failure mode.
 */
import { useCallback, useSyncExternalStore } from "react";

const KEY = "nob.seen";

/** Conversation id → when it was last opened, in milliseconds. */
type Marks = Record<string, number>;

let marks: Marks | null = null;
const listeners = new Set<() => void>();

function read(): Marks {
  if (marks) return marks;
  marks = {};
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        for (const [id, at] of Object.entries(parsed as Record<string, unknown>)) {
          if (typeof at === "number" && Number.isFinite(at)) marks[id] = at;
        }
      }
    }
  } catch {
    // A browser that refuses storage still gets a working list; it just always
    // reads as unread, which is the safe direction to be wrong in.
  }
  return marks;
}

function announce(): void {
  for (const listener of listeners) listener();
}

/** When this browser last opened that conversation, or `0` for never. */
export function seenAt(id: string): number {
  return read()[id] ?? 0;
}

/**
 * One if the conversation has moved since it was last opened.
 *
 * A count rather than a boolean because the row renders a badge and the sort
 * reads a number — but it is only ever 0 or 1, and it says so: there is no
 * per-message tally to count against, and a fabricated "3" would be a figure
 * nothing behind it could justify.
 */
export function unreadFor(id: string, at: number): number {
  if (!at) return 0;
  return at > seenAt(id) ? 1 : 0;
}

/** Opening a conversation is reading it. Idempotent; never moves a mark back. */
export function markSeen(id: string, at: number): void {
  const now = at || Date.now();
  const current = read();
  if ((current[id] ?? 0) >= now) return;
  // A NEW OBJECT, NOT AN EDIT IN PLACE (wave 82). The object is the store's
  // snapshot (`useSeen`), and a snapshot changed in place is a snapshot that
  // has not changed: the badge on the conversation somebody was reading stayed
  // lit until something else re-rendered the list (`md-highprofile`).
  marks = { ...current, [id]: now };
  try {
    localStorage.setItem(KEY, JSON.stringify(marks));
  } catch {
    // In memory for this session is still better than nothing.
  }
  announce();
}

/**
 * Test-only, and the sign-out path: the marks are this person's, not the app's.
 *
 * The cache is dropped rather than emptied, so the next read goes back to
 * storage. Emptying it in place would leave a stale `{}` in front of whatever
 * is written next — which is the difference between "forget" and "pretend".
 */
export function resetSeen(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
  marks = null;
  announce();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The marks as React state.
 *
 * The snapshot is the object identity, which only changes when `markSeen`
 * writes — so a render that merely reads a dozen ids does not resubscribe, and
 * marking one conversation re-renders the list that shows all of them.
 */
export function useSeen(): {
  unreadFor: (id: string, at: number) => number;
  markSeen: (id: string, at: number) => void;
} {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => read(),
    () => read(),
  );
  return {
    // A NEW FUNCTION WHENEVER THE MARKS MOVE (wave 82). The list derives its
    // unread counts inside a memo keyed on this, and a function that never
    // changed kept the counts it computed before the conversation was opened.
    // The snapshot is the dependency, deliberately: `unreadFor` reads it.
    unreadFor: useCallback((id: string, at: number) => unreadFor(id, at), [snapshot]),
    // Stable across renders: the store is module-level, so nothing here closes
    // over anything a dependency array would have to watch.
    markSeen: useCallback(markSeen, []),
  };
}
